import Combobox from '@/components/Combobox';
import { SelectionDisplayStyle } from '@/components/Combobox/types';
import { CountryFlagWithLabel } from '@/components/CountryFlag';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { CountryCode } from '@/constants/country';
import { defaultNS, useTranslation } from '@/lib/i18n';

import Input from '../Input';

import type { PhoneValue } from './state.machine';
import { useInputPhone } from './useInputPhone';

export interface InputPhoneNumberProps {
  /** 電話番号の値 */
  value: PhoneValue;
  /** 値が変更された時のコールバック */
  onChange: (value: PhoneValue) => void;
  /** フォーカスが外れた時のコールバック */
  onBlur?: () => void;
  /** コンポーネントの幅 */
  width?: number | string;
  /** デフォルトの国コード（デフォルト: "JP"） */
  defaultCountry?: CountryCode;
  /** プレースホルダー（任意） */
  placeholder?: string;
  /** 無効化フラグ（任意） */
  disabled?: boolean;
  /** 読み取り専用フラグ（任意） */
  readOnly?: boolean;
  /** 必須フラグ（任意） */
  required?: boolean;
}

export const InputPhoneNumber = ({
  value,
  onChange,
  onBlur,
  width = 260,
  placeholder,
  disabled,
  readOnly,
  required = false,
}: InputPhoneNumberProps) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'component.inputPhoneNumber',
  });

  const {
    formattedNumber,
    error,
    selectedDialCode,
    isOpen,
    searchText,
    countryOptions,
    handlePhoneNumberChange,
    handleDialCodeChange,
    handleSearchChange,
    togglePopover,
  } = useInputPhone(value, onChange, required);

  return (
    <VStack>
      <HStack>
        <Input
          type="tel"
          name="telephone"
          autoComplete="tel"
          ignorePrefixClick={true}
          prefix={
            <Popover open={isOpen} onOpenChange={togglePopover}>
              <PopoverTrigger asChild>
                <HStack className="gap-1">
                  <span>+{selectedDialCode}</span>
                  <button type="button" className="flex items-center gap-1">
                    <Icon iconName="ChevronDownIcon" size={12} />
                  </button>
                </HStack>
              </PopoverTrigger>
              <PopoverContent className="w-popover-lg p-0">
                <Combobox<{
                  key: CountryCode;
                  label: string;
                  value: string;
                }>
                  type="single"
                  options={countryOptions}
                  value={selectedDialCode}
                  onValueChange={handleDialCodeChange}
                  search={searchText}
                  onSearchChange={handleSearchChange}
                  searchPlaceholder={t('searchByCountryName')}
                  noResultsMessage={t('noResultsMessage')}
                  showCheckbox={false}
                  renderOption={({
                    option,
                  }: {
                    option: {
                      key: CountryCode;
                      label: string;
                      value: string;
                    };
                  }) => (
                    <CountryFlagWithLabel
                      countryCode={option.key}
                      label={option.label}
                    />
                  )}
                  selectionDisplayStyle={SelectionDisplayStyle.hidden}
                />
              </PopoverContent>
            </Popover>
          }
          value={formattedNumber}
          width={width}
          onChange={handlePhoneNumberChange}
          onBlur={onBlur}
          error={!!error}
          placeholder={placeholder || '000-0000-0000'}
          disabled={disabled}
          readOnly={readOnly}
        />
      </HStack>
      {error && (
        <>
          <Spacer variant="xxs" />
          <Text variant="labelReg" className="text-error-fill">
            {error}
          </Text>
        </>
      )}
    </VStack>
  );
};
